import { useState, useMemo, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search, Users, Shield, UserCircle, Loader2, X, Eye,
  CheckCircle2, Clock, AlertTriangle, XCircle, CreditCard,
  Globe, CalendarDays,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { cn } from '../lib/cn';

/* ── Types ──────────────────────────────────────────────── */
type PlanState =
  | 'none'
  | 'trial'
  | 'trial_expired'
  | 'active'
  | 'past_due'
  | 'canceled_active'
  | 'canceled_expired';

type AdminUser = {
  id: string;
  email: string;
  role: 'user' | 'admin';
  createdAt: string;
  lastLoginAt: string | null;
  plan: { name: string | null; state: PlanState; daysLeft: number | null };
};

type SubStatus = 'pending' | 'trialing' | 'active' | 'past_due' | 'canceled';

type UserDetail = {
  user: {
    id: string;
    email: string;
    role: 'user' | 'admin';
    timezone: string;
    createdAt: string;
    lastLoginAt: string | null;
  };
  subscription: {
    id: string;
    status: SubStatus;
    billingPeriod: 'monthly' | 'yearly';
    trialEndsAt: string | null;
    currentPeriodEnd: string | null;
    canceledAt: string | null;
    createdAt: string;
    plan: {
      id: string;
      name: string;
      priceMonthly: number;
      priceYearly: number;
      currency: string;
      networks: { platform: string; maxAccounts: number }[];
    } | null;
  } | null;
  socialAccountsCount: number;
};

/* ── Plan / subscription badge (admin list) ─────────────── */
const PLAN_BADGE: Record<PlanState, { label: string; cls: string; desc: string }> = {
  none:             { label: 'Sin plan',        cls: 'bg-slate-100 text-slate-500',     desc: 'Registrado pero nunca completó el checkout. No tiene acceso.' },
  trial:            { label: 'Prueba',          cls: 'bg-blue-50 text-blue-700',        desc: 'En prueba gratuita, antes del primer cobro. Tiene acceso.' },
  trial_expired:    { label: 'Prueba expirada', cls: 'bg-red-50 text-red-700',          desc: 'La prueba terminó sin convertirse en pago. Sin acceso.' },
  active:           { label: 'Activo',          cls: 'bg-emerald-50 text-emerald-700',  desc: 'Suscripción de pago activa.' },
  past_due:         { label: 'Pago fallido',    cls: 'bg-orange-50 text-orange-700',    desc: 'El cobro fue rechazado. Sin acceso hasta regularizar el pago.' },
  canceled_active:  { label: 'Cancelada',       cls: 'bg-red-50 text-red-700',          desc: 'Cancelada, pero mantiene acceso hasta que termine el periodo ya pagado.' },
  canceled_expired: { label: 'Cancelada',       cls: 'bg-red-50 text-red-700',          desc: 'Cancelada y el periodo ya terminó. Sin acceso.' },
};

function PlanBadge({ plan }: { plan: AdminUser['plan'] }) {
  const b = PLAN_BADGE[plan.state];
  let label = b.label;
  if (plan.state === 'trial' && plan.daysLeft != null) label = `Prueba · ${plan.daysLeft}d`;
  if (plan.state === 'canceled_active' && plan.daysLeft != null) label = `Cancelada · ${plan.daysLeft}d`;
  const showName =
    plan.name && (plan.state === 'active' || plan.state === 'trial' || plan.state === 'canceled_active');
  // Native tooltip: what the state means + plan name + days left when relevant.
  let title = b.desc;
  if (plan.name) title += ` Plan: ${plan.name}.`;
  if (plan.daysLeft != null && (plan.state === 'trial' || plan.state === 'canceled_active')) {
    title += ` Quedan ${plan.daysLeft} días.`;
  }
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      <span className={cn('inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium cursor-default', b.cls)}>
        {label}
      </span>
      {showName && <span className="text-xs text-slate-400">{plan.name}</span>}
    </span>
  );
}

type Plan = {
  id: string;
  name: string;
  priceMonthly: number;
  priceYearly: number;
  currency: string;
};

/* ── Status display ─────────────────────────────────────── */
const STATUS_META: Record<SubStatus, { label: string; cls: string; Icon: typeof Clock }> = {
  pending:  { label: 'Pago pendiente',   cls: 'bg-amber-50 text-amber-700 border-amber-200',      Icon: Clock },
  trialing: { label: 'Prueba gratuita',  cls: 'bg-brand-50 text-brand-700 border-brand-200',      Icon: Clock },
  active:   { label: 'Activa',           cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  past_due: { label: 'Pago fallido',     cls: 'bg-red-50 text-red-700 border-red-200',            Icon: AlertTriangle },
  canceled: { label: 'Cancelada',        cls: 'bg-slate-100 text-slate-500 border-slate-200',     Icon: XCircle },
};

/* ── Helpers ────────────────────────────────────────────── */
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

const fmtMoney = (cents: number, currency: string) =>
  (cents / 100).toLocaleString('es-ES', { style: 'currency', currency });

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function relativeTime(iso: string): string {
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins  < 1)  return 'ahora mismo';
  if (mins  < 60) return `hace ${mins} min`;
  if (hours < 24) return `hace ${hours} h`;
  if (days  < 30) return `hace ${days} d`;
  return new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

/* Today as YYYY-MM-DD for min attr on date input */
const todayStr = new Date().toISOString().slice(0, 10);

/* ── Main page ──────────────────────────────────────────── */
export function AdminUsers() {
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<{ users: AdminUser[] }>('/api/admin/users'),
    refetchInterval: 60_000,
  });

  const users = data?.users ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => u.email.toLowerCase().includes(q));
  }, [users, search]);

  const adminCount = users.filter((u) => u.role === 'admin').length;
  const userCount  = users.filter((u) => u.role === 'user').length;

  return (
    <div className="space-y-6">
      {/* header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Usuarios</h1>
          <p className="mt-1 text-sm text-slate-500">
            {isLoading ? '…' : `${users.length} registrados · ${adminCount} admin · ${userCount} usuarios`}
          </p>
        </div>
      </div>

      {/* search */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        <input
          type="text"
          placeholder="Buscar por email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full bg-white border border-slate-200 rounded-xl pl-9 pr-9 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent shadow-sm"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> Cargando usuarios…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 flex flex-col items-center justify-center gap-2 text-slate-400">
            <Users size={28} strokeWidth={1.5} />
            <p className="text-sm">
              {search ? 'Sin resultados para esa búsqueda.' : 'No hay usuarios registrados.'}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100">
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider hidden sm:table-cell">
                  Rol
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider hidden md:table-cell">
                  Registro
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-medium text-slate-400 uppercase tracking-wider hidden lg:table-cell">
                  Último acceso
                </th>
                <th className="px-5 py-3.5 text-right text-xs font-medium text-slate-400 uppercase tracking-wider w-16">

                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => (
                <tr
                  key={u.id}
                  className={`${
                    i < filtered.length - 1 ? 'border-b border-slate-50' : ''
                  } hover:bg-slate-50 transition-colors`}
                >
                  {/* email + avatar */}
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        {u.role === 'admin' ? (
                          <Shield size={14} className="text-brand-600" />
                        ) : (
                          <UserCircle size={15} className="text-slate-400" />
                        )}
                      </div>
                      <span className="text-slate-700 truncate max-w-[200px] font-medium">
                        {u.email}
                      </span>
                    </div>
                  </td>

                  {/* role badge */}
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                        u.role === 'admin'
                          ? 'bg-brand-100 text-brand-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {u.role}
                    </span>
                  </td>

                  {/* plan / subscription state */}
                  <td className="px-5 py-3.5">
                    <PlanBadge plan={u.plan} />
                  </td>

                  {/* registration date */}
                  <td className="px-5 py-3.5 hidden md:table-cell">
                    <span className="text-slate-500 text-xs">{formatDate(u.createdAt)}</span>
                  </td>

                  {/* last login */}
                  <td className="px-5 py-3.5 hidden lg:table-cell">
                    <span className="text-slate-500 text-xs">
                      {u.lastLoginAt ? relativeTime(u.lastLoginAt) : '—'}
                    </span>
                  </td>

                  {/* eye button */}
                  <td className="px-4 py-3.5 text-right">
                    <button
                      onClick={() => setDetailId(u.id)}
                      title="Ver detalle"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-brand-600 transition"
                    >
                      <Eye size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* result count when searching */}
      {search && !isLoading && (
        <p className="text-xs text-slate-400">
          {filtered.length} resultado{filtered.length !== 1 ? 's' : ''} para «{search}»
        </p>
      )}

      {/* detail modal */}
      {detailId && (
        <UserDetailModal userId={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  );
}

/* ── User detail modal ──────────────────────────────────── */
function UserDetailModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', userId],
    queryFn: () => api<UserDetail>(`/api/admin/users/${userId}`),
  });

  const { data: plansData } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: () => api<{ plans: Plan[] }>('/api/admin/plans'),
  });

  const plans = plansData?.plans ?? [];

  const [planId, setPlanId] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const assign = useMutation({
    mutationFn: () =>
      api(`/api/admin/users/${userId}/subscription`, {
        method: 'POST',
        body: JSON.stringify({ planId, endsAt }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'users', userId] });
      qc.invalidateQueries({ queryKey: ['admin', 'users'] });
      setPlanId('');
      setEndsAt('');
      setFormError(null);
    },
    onError: (err) => {
      setFormError(
        err instanceof ApiError ? err.message || 'Error al asignar el plan.' : 'Error al asignar el plan.',
      );
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!planId) { setFormError('Selecciona un plan.'); return; }
    if (!endsAt) { setFormError('Introduce una fecha de expiración.'); return; }
    assign.mutate();
  }

  const detail = data;

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <h2 className="font-bold text-slate-900">Detalle de cuenta</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition">
            <X size={18} />
          </button>
        </div>

        {isLoading ? (
          <div className="py-16 flex items-center justify-center gap-2 text-slate-400 text-sm">
            <Loader2 size={16} className="animate-spin" /> Cargando…
          </div>
        ) : !detail ? (
          <div className="py-16 flex items-center justify-center text-sm text-slate-500">
            No se pudo cargar el usuario.
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* ── User info ── */}
            <section className="bg-slate-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0">
                  {detail.user.role === 'admin' ? (
                    <Shield size={16} className="text-brand-600" />
                  ) : (
                    <UserCircle size={18} className="text-slate-400" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{detail.user.email}</p>
                  <span className={cn(
                    'inline-flex text-[11px] font-medium rounded-full px-2 py-0.5 mt-0.5',
                    detail.user.role === 'admin'
                      ? 'bg-brand-100 text-brand-700'
                      : 'bg-slate-100 text-slate-500',
                  )}>
                    {detail.user.role}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <InfoRow icon={<CalendarDays size={13} />} label="Registrado" value={fmtDate(detail.user.createdAt)} />
                <InfoRow
                  icon={<Clock size={13} />}
                  label="Último acceso"
                  value={detail.user.lastLoginAt ? relativeTime(detail.user.lastLoginAt) : '—'}
                />
                <InfoRow icon={<Globe size={13} />} label="Zona horaria" value={detail.user.timezone} />
                <InfoRow
                  icon={<CreditCard size={13} />}
                  label="Cuentas sociales"
                  value={String(detail.socialAccountsCount)}
                />
              </div>
            </section>

            {/* ── Current subscription ── */}
            <section>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Suscripción actual
              </h3>
              {detail.subscription ? (
                <SubscriptionSummary sub={detail.subscription} />
              ) : (
                <div className="flex items-center gap-2.5 bg-slate-50 rounded-xl p-3.5 text-sm text-slate-500">
                  <CreditCard size={16} className="shrink-0 text-slate-400" />
                  Sin suscripción activa
                </div>
              )}
            </section>

            {/* ── Assign plan ── */}
            <section>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
                Asignar plan manualmente
              </h3>
              <form onSubmit={onSubmit} className="space-y-3">
                <div>
                  <label className="label" htmlFor="assign-plan">Plan</label>
                  <select
                    id="assign-plan"
                    value={planId}
                    onChange={(e) => setPlanId(e.target.value)}
                    className="input"
                  >
                    <option value="">— Selecciona un plan —</option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name} · {fmtMoney(p.priceMonthly, p.currency)}/mes
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="label" htmlFor="assign-ends">Fecha de expiración</label>
                  <input
                    id="assign-ends"
                    type="date"
                    min={todayStr}
                    value={endsAt}
                    onChange={(e) => setEndsAt(e.target.value)}
                    className="input"
                  />
                  <p className="mt-1 text-xs text-slate-400">
                    El acceso del usuario expirará a medianoche (UTC) de esa fecha.
                  </p>
                </div>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">
                    {formError}
                  </p>
                )}

                {assign.isSuccess && (
                  <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2 flex items-center gap-1.5">
                    <CheckCircle2 size={14} /> Plan asignado correctamente.
                  </p>
                )}

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={assign.isPending}
                    className="btn-primary gap-2"
                  >
                    {assign.isPending && <Loader2 size={14} className="animate-spin" />}
                    {assign.isPending ? 'Asignando…' : 'Asignar plan'}
                  </button>
                </div>
              </form>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Subscription summary card ──────────────────────────── */
function SubscriptionSummary({
  sub,
}: {
  sub: NonNullable<UserDetail['subscription']>;
}) {
  const meta = STATUS_META[sub.status] ?? STATUS_META.canceled;
  const { Icon } = meta;

  return (
    <div className="bg-slate-50 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-semibold text-slate-900 text-sm">
          {sub.plan?.name ?? 'Plan desconocido'}
        </span>
        <span className={cn('inline-flex items-center gap-1 text-[11px] font-medium rounded-full border px-2 py-0.5', meta.cls)}>
          <Icon size={11} /> {meta.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <InfoRow icon={<CreditCard size={13} />} label="Facturación" value={sub.billingPeriod === 'yearly' ? 'Anual' : 'Mensual'} />
        {sub.status === 'trialing' && sub.trialEndsAt && (
          <InfoRow icon={<Clock size={13} />} label="Fin de prueba" value={fmtDate(sub.trialEndsAt)} />
        )}
        {sub.currentPeriodEnd && (
          <InfoRow icon={<CalendarDays size={13} />} label="Expira" value={fmtDate(sub.currentPeriodEnd)} />
        )}
        {sub.canceledAt && (
          <InfoRow icon={<XCircle size={13} />} label="Cancelada" value={fmtDate(sub.canceledAt)} />
        )}
      </div>
    </div>
  );
}

/* ── Tiny info row ──────────────────────────────────────── */
function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-slate-400 shrink-0">{icon}</span>
      <span className="text-slate-400 shrink-0">{label}:</span>
      <span className="text-slate-700 font-medium truncate">{value}</span>
    </div>
  );
}
